import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  fetchSession,
  setAuthToken,
} from '@/api/client';
import type { AuthResult, AuthUser } from '@/types/api';

/**
 * Auth state — backend is SSOT (CORE_ENGINEERING_PRINCIPLES).
 * Web: httpOnly cookie persists across refresh; bootstrap via GET /api/auth/session.
 * Bearer token in memory is for Socket.io only (cookie is httpOnly).
 */
export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null);
  const user = ref<AuthUser | null>(null);
  const expiresAt = ref<string | null>(null);
  const sessionChecked = ref(false);

  const isAuthenticated = computed(() => Boolean(token.value && user.value));

  function applySession(result: AuthResult): void {
    token.value = result.token;
    user.value = result.user;
    expiresAt.value = result.expiresAt;
    setAuthToken(result.token);
  }

  /** Restore session from httpOnly cookie on cold load / refresh. */
  async function restoreSession(): Promise<void> {
    try {
      const result = await fetchSession();
      applySession(result);
    } catch {
      token.value = null;
      user.value = null;
      expiresAt.value = null;
      setAuthToken(null);
    } finally {
      sessionChecked.value = true;
    }
  }

  async function ensureSessionChecked(): Promise<void> {
    if (!sessionChecked.value) {
      await restoreSession();
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const result = await apiLogin({ email, password });
    applySession(result);
    sessionChecked.value = true;
  }

  async function register(
    email: string,
    password: string,
    displayName?: string,
  ): Promise<void> {
    const result = await apiRegister({ email, password, displayName });
    applySession(result);
    sessionChecked.value = true;
  }

  async function logout(): Promise<void> {
    try {
      await apiLogout();
    } finally {
      token.value = null;
      user.value = null;
      expiresAt.value = null;
      sessionChecked.value = true;
      setAuthToken(null);
    }
  }

  return {
    token,
    user,
    expiresAt,
    sessionChecked,
    isAuthenticated,
    login,
    register,
    logout,
    applySession,
    restoreSession,
    ensureSessionChecked,
  };
});
